import { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import firebase, { db } from './firebase';
import { AuthContext } from '../services/contexts/AuthContext';
import { ContentSections } from './ContentSections';
import { ChristmasWheel } from './ChristmasWheel';
import { NotFound } from './NotFound';
import { ChristmasInvite } from './ChristmasInvite';
import { GoogleSignIn } from './GoogleSignIn';
import {
  GAME, SPINS, BONUS, INVITES, INVITE_PARAM, isInviteCode, inviteTickets, ADMIN_EMAIL, bgDay, dayStart, nextDayStart, phaseAt, spinId, outcomeOf,
  jitterFor, wheelTarget, rewardTable, dateLabel, START_DAY, END_DAY, DRAW_DAY,
} from '../utils/christmasGame';
import '../styles/content.css';
import '../styles/christmas-game.css';

const COUNT = GAME.segments.length;
const SLICE = 360 / COUNT;
const PAGE_URL = `https://stamsyart.bg${GAME.path}`;

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const LAND_MS = () => (reducedMotion() ? 400 : 4600);
const SPIN_UP_MS = 1000;

const longDate = day => dateLabel(day, { weekday: 'long', day: 'numeric', month: 'long' });
const ticketWord = n => (n === 1 ? 'билет' : 'билета');

// Кодът от линка за покана се пази, докато човекът се регистрира и завърти
// за първи път – регистрацията минава през други страници.
const INVITE_KEY = 'sa-xg-invite';
const readInvite = () => {
  try { return localStorage.getItem(INVITE_KEY); } catch { return null; }
};
const forgetInvite = () => {
  try { localStorage.removeItem(INVITE_KEY); } catch { /* няма значение */ }
};

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function Countdown({ to, now }) {
  const diff = Math.max(0, to - now);
  const parts = [
    ['дни', Math.floor(diff / 86400000)],
    ['часа', Math.floor((diff % 86400000) / 3600000)],
    ['мин', Math.floor((diff % 3600000) / 60000)],
    ['сек', Math.floor((diff % 60000) / 1000)],
  ].filter(([, value], i) => i > 0 || value > 0);
  return (
    <div className="xg-countdown" aria-live="off">
      {parts.map(([label, value]) => (
        <div key={label}>
          <strong>{String(value).padStart(2, '0')}</strong>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

const openChat = () => window.Tawk_API?.maximize?.();

const CONFETTI_COLORS = ['#f4c95d', '#ffffff', '#e8577e', '#5fd0a4', '#b99be0'];

/** Кратък дъжд от конфети при печалба. Без анимации – нищо. */
function Confetti() {
  const [pieces] = useState(() => Array.from({ length: 42 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 1.6 + Math.random() * 1.2,
    drift: (Math.random() - 0.5) * 160,
    spin: (Math.random() - 0.5) * 900,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    wide: i % 3 === 0,
  })));
  if (reducedMotion()) return null;
  return (
    <div className="xg-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className={p.wide ? 'xg-confetti-wide' : undefined}
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            '--drift': `${p.drift}px`,
            '--spin': `${p.spin}deg`,
          }}
        />
      ))}
    </div>
  );
}

/** „Опитай пак“ и „Усмивка“ – без код, но с билет. */
function MissCard({ miss }) {
  return (
    <div className="xg-reward xg-miss">
      <p className="xg-reward-title">{miss.title}</p>
      <p className="xg-reward-cond">Този път без награда – утре колелото те чака отново.</p>
      <p className="xg-actions">
        <Link className="xg-btn xg-btn-ghost" to="/categories/koleda">Разгледай коледните подаръци</Link>
      </p>
    </div>
  );
}

/** Какво е спечелено, до кога важи и как се използва. */
function RewardCard({ reward, today }) {
  const [copied, setCopied] = useState(false);
  const expired = today > reward.validUntil;
  const copy = () => {
    navigator.clipboard?.writeText(reward.code).then(() => setCopied(true)).catch(() => {});
  };
  return (
    <div className="xg-reward">
      <p className="xg-reward-title">{reward.title}</p>
      <p className="xg-reward-cond">{reward.condition}</p>
      <div className="xg-code">
        <span>Твоят код</span>
        <strong>{reward.code}</strong>
        <small>
          {expired ? 'Кодът е изтекъл.' : `Важи до ${longDate(reward.validUntil)} включително.`}
        </small>
      </div>
      {!expired && (
        <>
          <ol className="xg-howto">
            <li>Избери продукт от каталога.</li>
            <li>Натисни „Поръчай тук“ и напиши кода в чата.</li>
          </ol>
          <p className="xg-actions">
            <button type="button" className="xg-btn" onClick={copy}>
              {copied ? 'Кодът е копиран ✓' : 'Копирай кода'}
            </button>
            <Link className="xg-btn xg-btn-ghost" to="/catalog">Към каталога</Link>
            <button type="button" className="xg-btn xg-btn-ghost" onClick={openChat}>Пиши ни</button>
          </p>
        </>
      )}
    </div>
  );
}

const withTimeout = (promise, ms) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
]);

const displayName = user =>
  (user.displayName || localStorage.getItem('username') || user.email.split('@')[0]).slice(0, 60);

const docs = snap => snap.docs.map(d => ({ id: d.id, ...d.data() }));

export const ChristmasGame = () => {
  const { user, authLoading } = useContext(AuthContext);
  const now = useNow();
  const phase = phaseAt(now);
  const today = bgDay(now);

  const [spins, setSpins] = useState(null);
  const [bonuses, setBonuses] = useState([]);
  const [invited, setInvited] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [spinError, setSpinError] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [stage, setStage] = useState('idle');
  const [fresh, setFresh] = useState(false);
  const cruise = useRef(null);
  const settled = useRef(false);

  const uid = user?.uid;
  const email = user?.email;

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get(INVITE_PARAM);
    if (!isInviteCode(code)) return;
    try { localStorage.setItem(INVITE_KEY, code); } catch { /* без покана */ }
    // Махаме кода от адреса, за да не отиде в статистиката и да не се
    // сподели по погрешка чуждият линк.
    window.history.replaceState(window.history.state, '', window.location.pathname);
  }, []);

  useEffect(() => {
    setSpins(null);
    setBonuses([]);
    setInvited(0);
    setLoadError(null);
    settled.current = false;
    if (!uid) return;
    Promise.all([
      db.collection(SPINS).where('uid', '==', uid).get(),
      db.collection(BONUS).where('email', '==', email).get(),
    ])
      .then(([spinSnap, bonusSnap]) => {
        setSpins(docs(spinSnap));
        setBonuses(docs(bonusSnap));
      })
      .catch(() => setLoadError('Не успяхме да заредим твоите завъртания. Опресни страницата.'));
    db.collection(INVITES).where('inviter', '==', uid).get()
      .then(snap => setInvited(snap.size))
      .catch(() => {});
  }, [uid, email]);

  useEffect(() => () => clearTimeout(cruise.current), []);

  const todaySpin = spins?.find(s => s.day === today) || null;

  // При отваряне колелото стои на днешния резултат, ако вече е завъртяно.
  useEffect(() => {
    if (settled.current || !todaySpin || stage !== 'idle') return;
    settled.current = true;
    const { index } = outcomeOf(todaySpin);
    setRotation(wheelTarget(0, index, COUNT, jitterFor(todaySpin.at, SLICE), 0));
  }, [todaySpin, stage]);

  const mine = (spins || []).map(s => ({ spin: s, ...outcomeOf(s) }));
  const orderTickets = bonuses.reduce((sum, b) => sum + b.tickets, 0);
  const tickets = mine.reduce((sum, o) => sum + o.tickets, 0) + orderTickets + inviteTickets(invited);
  const codes = mine.filter(o => o.reward).sort((a, b) => b.spin.day - a.spin.day);

  const spin = async () => {
    if (!user || todaySpin || stage !== 'idle') return;
    setSpinError(null);
    setFresh(false);
    settled.current = true;
    const firstSpin = spins?.length === 0;

    const started = Date.now();
    setStage('up');
    setRotation(r => r + 720);
    const keepTurning = () => {
      setStage('cruise');
      setRotation(r => r + 720);
      cruise.current = setTimeout(keepTurning, 500);
    };
    cruise.current = setTimeout(keepTurning, SPIN_UP_MS);

    const ref = db.collection(SPINS).doc(spinId(user.uid, today));
    try {
      await withTimeout(ref.set({
        uid: user.uid,
        email: user.email,
        name: displayName(user),
        day: today,
        at: firebase.firestore.FieldValue.serverTimestamp(),
      }), 15000);
      const snap = await withTimeout(ref.get({ source: 'server' }), 15000);
      const saved = { id: snap.id, ...snap.data() };

      // Поканата се брои само за първото завъртане и само след като то е записано.
      const inviter = readInvite();
      if (firstSpin && isInviteCode(inviter) && inviter !== user.uid) {
        db.collection(INVITES).doc(user.uid).set({
          inviter,
          day: today,
          at: firebase.firestore.FieldValue.serverTimestamp(),
        }).catch(() => {});
      }
      forgetInvite();
      const outcome = outcomeOf(saved);

      await new Promise(r => setTimeout(r, Math.max(0, SPIN_UP_MS - (Date.now() - started))));
      clearTimeout(cruise.current);
      setStage('land');
      setRotation(r => wheelTarget(r, outcome.index, COUNT, jitterFor(saved.at, SLICE)));
      await new Promise(r => setTimeout(r, LAND_MS()));

      setStage('idle');
      setSpins(list => [...(list || []), saved]);
      setFresh(true);
    } catch (err) {
      clearTimeout(cruise.current);
      setStage('idle');
      // Отказ най-често значи, че днес вече е завъртяно от друго устройство.
      const again = await db.collection(SPINS).where('uid', '==', user.uid).get().catch(() => null);
      const list = again ? docs(again) : null;
      if (list) setSpins(list);
      if (list?.some(s => s.day === today)) {
        settled.current = false;
        return;
      }
      setSpinError(err.message === 'timeout'
        ? 'Няма връзка със сървъра. Провери интернета и опитай пак.'
        : 'Колелото не се завъртя. Провери дали датата и часът на устройството са верни и опитай пак.');
    }
  };

  const busy = stage !== 'idle';
  const canSpin = phase === 'live' && !!user && !!spins && !todaySpin && !loadError;
  const loginLink = `/login?next=${encodeURIComponent(GAME.path)}`;
  const registerLink = `/register?next=${encodeURIComponent(GAME.path)}`;

  let action;
  if (phase === 'hidden' || phase === 'soon') {
    action = (
      <div className="xg-status">
        <p className="xg-status-title">Колелото тръгва на {dateLabel(START_DAY)}</p>
        <Countdown to={dayStart(START_DAY)} now={now} />
        {!user && !authLoading && (
          <p>
            <Link className="xg-btn" to={registerLink}>Регистрирай се предварително</Link>
          </p>
        )}
      </div>
    );
  } else if (phase === 'ended') {
    action = (
      <div className="xg-status">
        <p className="xg-status-title">Завъртанията приключиха</p>
        <p>Голямата награда се тегли на {dateLabel(DRAW_DAY)}. Победителят ще получи имейл и ще бъде обявен във Facebook.</p>
      </div>
    );
  } else if (authLoading) {
    action = <p className="xg-status">Зареждане…</p>;
  } else if (!user) {
    action = (
      <div className="xg-status">
        <p className="xg-status-title">Влез в профила си, за да завъртиш</p>
        <GoogleSignIn />
        <p className="xg-actions">
          <Link className="xg-btn" to={loginLink}>Вход</Link>
          <Link className="xg-btn xg-btn-ghost" to={registerLink}>Регистрация</Link>
        </p>
      </div>
    );
  } else if (loadError) {
    action = <p className="xg-error" role="alert">{loadError}</p>;
  } else if (!spins) {
    action = <p className="xg-status">Зареждане…</p>;
  } else if (todaySpin && !busy) {
    const result = outcomeOf(todaySpin);
    action = (
      <div className="xg-status">
        <p className="xg-status-title" aria-live="polite">
          {result.reward ? (fresh ? 'Честито! Спечели:' : 'Днес спечели:') : (fresh ? 'Почти!' : 'Днес се падна:')}
        </p>
        {result.reward ? <RewardCard reward={result.reward} today={today} /> : <MissCard miss={result.miss} />}
        <p className="xg-ticket-note">
          + {result.tickets} {ticketWord(result.tickets)} за тегленето на детската тениска на {dateLabel(DRAW_DAY)}
        </p>
        <p>Следващото завъртане е след</p>
        <Countdown to={nextDayStart(now)} now={now} />
      </div>
    );
  } else {
    action = (
      <div className="xg-status">
        <button type="button" className="xg-btn xg-btn-big" onClick={spin} disabled={busy}>
          {busy ? 'Върти се…' : 'Завърти колелото'}
        </button>
        <p className="xg-hint">Печели отстъпка, безплатна доставка или подарък към поръчката.</p>
        {spinError && <p className="xg-error" role="alert">{spinError}</p>}
      </div>
    );
  }

  // Докато играта не е публикувана, я вижда само администраторът.
  const isAdmin = user?.email === ADMIN_EMAIL;
  if (!GAME.published && !isAdmin) return authLoading ? null : <NotFound />;

  return (
    <>
      <Helmet>
        <title>{GAME.title}</title>
        <meta name="description" content={GAME.description} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:title" content={GAME.title} />
        <meta property="og:description" content={GAME.description} />
      </Helmet>

      <div className="xg-page">
        <nav className="catalog-breadcrumb">
          <Link to="/">Начало</Link>
          <span>/</span>
          <span>{GAME.h1}</span>
        </nav>

        {!GAME.published && (
          <p className="xg-draft">
            Играта още не е публикувана – виждаш я, защото си администратор.
          </p>
        )}
        <header className="xg-hero">
          <h1>{GAME.h1}</h1>
          <p>{GAME.intro}</p>
        </header>

        <div className="xg-grid">
          <section className="xg-play">
            {fresh && todaySpin && outcomeOf(todaySpin).reward && <Confetti key={todaySpin.id} />}
            <ChristmasWheel rotation={rotation} stage={stage} onSpin={canSpin ? spin : undefined} />
            {action}
          </section>

          <aside className="xg-side">
            <div className="xg-card xg-prize">
              <span className="xg-eyebrow">Голямата награда</span>
              <p>{GAME.mainPrize}</p>
              <small>
                Тегли се на {dateLabel(DRAW_DAY)} между всички билети. Всяко завъртане носи {GAME.ticketsPerSpin} {ticketWord(GAME.ticketsPerSpin)}, всяка поръчка – още {GAME.ticketsPerOrder}, всяка поканена приятелка – още {GAME.ticketsPerInvite}.
              </small>
            </div>

            {(phase === 'soon' || phase === 'live') && <ChristmasInvite uid={uid} invited={invited} />}

            {user && spins && (
              <div className="xg-card">
                <span className="xg-eyebrow">Твоето участие</span>
                <div className="xg-stats">
                  <div><strong>{tickets}</strong><span>{ticketWord(tickets)}</span></div>
                  <div><strong>{spins.length}</strong><span>{spins.length === 1 ? 'завъртане' : 'завъртания'}</span></div>
                  <div><strong>{bonuses.length}</strong><span>{bonuses.length === 1 ? 'поръчка' : 'поръчки'}</span></div>
                  <div><strong>{invited}</strong><span>{invited === 1 ? 'покана' : 'покани'}</span></div>
                </div>
                {codes.length > 0 && (
                  <ul className="xg-rewards">
                    {codes.map(o => {
                      const expired = today > o.reward.validUntil;
                      return (
                        <li key={o.spin.id} className={expired || o.spin.redeemed ? 'xg-used' : ''}>
                          <strong>{o.reward.code}</strong> – {o.reward.title}
                          <small>
                            {o.spin.redeemed
                              ? 'Използван'
                              : expired ? 'Изтекъл' : `Важи до ${dateLabel(o.reward.validUntil)}`}
                          </small>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            <div className="xg-card">
              <span className="xg-eyebrow">Как се играе</span>
              <ol className="xg-steps">
                <li>Влез в профила си в stamsyart.bg.</li>
                <li>Завърти колелото – веднъж на ден, до {dateLabel(END_DAY)}.</li>
                <li>Използвай кода в рамките на {GAME.codeValidDays} дни – кажи го в чата при поръчка.</li>
                <li>При поръчка кажи и имейла си – получаваш още {GAME.ticketsPerOrder} билета.</li>
                <li>Покани приятелка с личния си линк – още {GAME.ticketsPerInvite} билета, когато завърти за първи път.</li>
              </ol>
              <Link className="xg-link" to="/categories/koleda">Разгледай коледните подаръци →</Link>
            </div>
          </aside>
        </div>

        <section className="content-section xg-legend">
          <h2>Какво има на колелото</h2>
          <div className="xg-table-wrap">
            <table>
              <thead>
                <tr><th>Поле</th><th>Условие</th><th>Шанс</th></tr>
              </thead>
              <tbody>
                {rewardTable().map(row => (
                  <tr key={row.key} className={row.noCode ? 'xg-legend-miss' : undefined}>
                    <td><strong>{row.title}</strong></td>
                    <td>{row.condition}</td>
                    <td>{row.percent.toLocaleString('bg-BG')}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="xg-legend-note">
            Кодът важи {GAME.codeValidDays} дни от завъртането. Един код – за една поръчка.
            Всяко завъртане, дори без награда, носи {GAME.ticketsPerSpin} {ticketWord(GAME.ticketsPerSpin)} за тегленето.
          </p>
        </section>

        <details className="xg-rules">
          <summary>Правила на играта</summary>
          <ContentSections sections={GAME.rules} />
        </details>
      </div>
    </>
  );
};
